// src/components/reports/PDFCreditoSimulador.jsx
import jsPDF from "jspdf";
import logo from "../../assets/logo2.png"; // Ajustar ruta según tu estructura

/**
 * Genera el PDF del simulador de crédito con el mismo formato que PDFCredito
 * @param {Object} vehicleData   Datos del vehículo
 * @param {Object} loanData      Datos del préstamo
 * @param {Object} results       Resultados del cálculo
 * @param {Object} configs       Configuración del simulador
 * @param {Object} params        Parámetros adicionales
 * @param {string} params.logo   Logo importado para usar en el PDF
 */
export function generarSimuladorPDF(vehicleData, loanData, results, configs, { logo: logoParam } = {}) {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.width;
  const pageHeight = doc.internal.pageSize.height;
  let yPosition = 30;

  // Colores corporativos (iguales al PDFCredito)
  const primaryColor = [0, 50, 38];      // #003226
  const secondaryColor = [219, 197, 168]; // #DBC5A8
  const grayColor = [107, 114, 128];
  const lightGray = [243, 244, 246];
  const greenAccent = [26, 95, 63];       // Verde para destacados

  const formatCurrency = (amount) =>
    new Intl.NumberFormat("es-AR", {
      style: "currency",
      currency: "ARS",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(Number(amount || 0));

  const formatDate = (dateString) => {
    try {
      const d = new Date(dateString);
      return d.toLocaleDateString("es-AR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return "N/A";
    }
  };

  const checkNewPage = (neededSpace = 20) => {
    if (yPosition + neededSpace > pageHeight - 30) {
      doc.addPage();
      yPosition = 30;
      return true;
    }
    return false;
  };

  // Header con color corporativo
  doc.setFillColor(primaryColor[0], primaryColor[1], primaryColor[2]);
  doc.rect(0, 0, pageWidth, 45, "F");

  const leftMargin = 20;
  const logoWidth = 50;
  const logoHeight = 20;

  // Logo en la parte superior izquierda (usar el logo pasado como parámetro o el importado)
  const logoToUse = logoParam || logo;
  if (logoToUse) {
    try {
      doc.addImage(logoToUse, "PNG", leftMargin, 8, logoWidth, logoHeight);
    } catch (e) {
      console.warn("No se pudo cargar el logo en el PDF:", e);
    }
  }

  // Línea separadora debajo del logo
  doc.setDrawColor(secondaryColor[0], secondaryColor[1], secondaryColor[2]);
  doc.setLineWidth(0.5);
  doc.line(leftMargin, 8 + logoHeight + 2, leftMargin + logoWidth, 8 + logoHeight + 2);

  // Texto debajo de la línea
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(12);
  doc.setFont(undefined, "normal");
  doc.text("Simulación de Crédito Prendario", leftMargin, 8 + logoHeight + 10);

  // Fecha del reporte a la derecha
  const fechaReporte = new Date().toLocaleDateString("es-AR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
  doc.setFontSize(10);
  doc.text(`Generado: ${fechaReporte}`, pageWidth - 60, 38);

  yPosition = 60;

  // Badge de "SIMULACIÓN"
  doc.setFillColor(greenAccent[0], greenAccent[1], greenAccent[2]);
  doc.roundedRect(pageWidth - 80, yPosition - 10, 60, 15, 3, 3, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(10);
  doc.setFont(undefined, "bold");
  doc.text("SIMULACIÓN", pageWidth - 75, yPosition);

  // Resetear color de texto
  doc.setTextColor(0, 0, 0);
  yPosition += 20;

  // Helper para secciones (igual que PDFCredito)
  const createSection = (title, content, bgColor = lightGray) => {
    checkNewPage(40);
    doc.setFillColor(bgColor[0], bgColor[1], bgColor[2]);
    doc.rect(20, yPosition - 8, pageWidth - 40, 20, "F");
    doc.setTextColor(primaryColor[0], primaryColor[1], primaryColor[2]);
    doc.setFontSize(14);
    doc.setFont(undefined, "bold");
    doc.text(title, 25, yPosition + 5);
    yPosition += 25;

    doc.setTextColor(0, 0, 0);
    doc.setFontSize(11);
    doc.setFont(undefined, "normal");
    content.forEach((item) => {
      checkNewPage();
      if (item.label && item.value !== undefined) {
        doc.setFont(undefined, "bold");
        doc.text(`${item.label}:`, 25, yPosition);
        doc.setFont(undefined, "normal");
        doc.text(String(item.value), 80, yPosition);
      } else if (item.text) {
        const lines = doc.splitTextToSize(item.text, pageWidth - 50);
        lines.forEach((line) => {
          checkNewPage();
          doc.text(line, 25, yPosition);
          yPosition += 6;
        });
        return;
      }
      yPosition += 8;
    });
    yPosition += 10;
  };

  // Título prominente
  doc.setFontSize(16);
  doc.setFont(undefined, "bold");
  doc.setTextColor(primaryColor[0], primaryColor[1], primaryColor[2]);
  doc.text("CRÉDITO PRENDARIO", 20, yPosition);
  yPosition += 20;

  // Información del Vehículo
  createSection("DATOS DEL VEHÍCULO", [
    { label: "Marca", value: vehicleData.marca || "-" },
    { label: "Modelo", value: vehicleData.modelo || "-" },
    { label: "Año", value: vehicleData.año || "-" },
    { label: "Versión", value: vehicleData.version || "-" },
    { label: "Valor del Vehículo", value: formatCurrency(loanData.vehicleValue) },
    { label: "Fuente del Precio", value: "Precio de referencia manual" },
  ]);

  // Información del Préstamo
  createSection(" DATOS DEL PRÉSTAMO", [
    { label: "Monto Solicitado", value: formatCurrency(loanData.amount) },
    { label: "Plazo", value: `${loanData.term} meses` },
    { label: "Tasa Anual", value: `${configs.prendario.tasaAnual}%` },
    { 
      label: "Porcentaje del Valor", 
      value: `${Math.round((loanData.amount / loanData.vehicleValue) * 100)}%` 
    },
  ]);

  // Cuota Mensual - Destacada (igual que en el PDFCredito)
  checkNewPage(50);
  doc.setFillColor(240, 248, 244); // Verde claro
  doc.rect(20, yPosition, pageWidth - 40, 35, "F");

  doc.setFontSize(16);
  doc.setTextColor(greenAccent[0], greenAccent[1], greenAccent[2]);
  doc.setFont(undefined, "bold");
  doc.text("CUOTA MENSUAL", pageWidth / 2, yPosition + 15, { align: "center" });

  doc.setFontSize(20);
  doc.text(formatCurrency(results.monthlyPayment), pageWidth / 2, yPosition + 28, { align: "center" });

  yPosition += 45;

  // Información Importante
  createSection(" INFORMACIÓN IMPORTANTE", [
    { text: "• Esta simulación es orientativa y está sujeta a aprobación crediticia." },
    { text: "• Las condiciones finales pueden variar según la evaluación crediticia del solicitante." },
    { text: "• El préstamo prendario requiere la constitución de prenda sobre el vehículo." },
    { text: "• Se requiere seguro del vehículo con cláusula prendaria a favor de la financiera." },
    { text: "• Los gastos de constitución de prenda y seguros son adicionales." },
    { text: "• Para obtener una cotización definitiva, complete la solicitud formal." },
  ]);

  // Footer en todas las páginas (igual que PDFCredito)
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setDrawColor(primaryColor[0], primaryColor[1], primaryColor[2]);
    doc.setLineWidth(0.5);
    doc.line(20, pageHeight - 25, pageWidth - 20, pageHeight - 25);
    doc.setTextColor(grayColor[0], grayColor[1], grayColor[2]);
    doc.setFontSize(8);
    doc.setFont(undefined, "normal");
    doc.text("Financiera Gamaan - Simulación de Crédito", 20, pageHeight - 15);
    doc.text(`Página ${i} de ${totalPages}`, pageWidth - 40, pageHeight - 15);
  }

  // Generar nombre del archivo
  const fechaCorta = new Date().toISOString().split("T")[0];
  const nombreArchivo = `Simulacion_Credito_Prendario_${vehicleData.marca}_${vehicleData.modelo}_${fechaCorta}.pdf`;
  
  // Guardar PDF
  doc.save(nombreArchivo);
  
  return nombreArchivo;
}
